import React from 'react';

function SkeletonBar({ className = '' }: { className?: string }) {
    return (
        <div className={`animate-pulse rounded bg-gray-200 dark:bg-gray-700 ${className}`} />
    );
}

interface BoardSkeletonProps {
    showList?: boolean;
    compact?: boolean;
    columns?: number;
    cardsPerColumn?: number;
    rows?: number;
}

function BoardColumnsSkeleton(props: { columns: number; cardsPerColumn: number }) {
    const { columns, cardsPerColumn } = props;
    const columnCount = Math.max(1, columns);
    const cardCount = Math.max(1, cardsPerColumn);
    const cardHeights = ['h-16', 'h-20', 'h-14', 'h-24'];

    return (
        <div className="flex items-start space-x-2">
            {Array.from({ length: columnCount }).map((_, columnIndex) => (
                <div
                    key={columnIndex}
                    className="w-[280px] shrink-0 flex flex-col justify-between bg-blue-100 dark:bg-blue-900/40 border rounded-lg p-1 overflow-hidden"
                >
                    <div className="shrink-0">
                        <div className="flex flex-col space-y-1 font-bold p-1 rounded bg-gray-50 dark:bg-gray-900">
                            <SkeletonBar className="h-4 w-28" />
                        </div>
                    </div>
                    <div className="flex-1 space-y-2 p-1 mt-1">
                        {Array.from({ length: cardCount }).map((_, cardIndex) => (
                            <div
                                key={cardIndex}
                                className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900"
                            >
                                <SkeletonBar className={`${cardHeights[cardIndex % cardHeights.length]} w-full`} />
                                <SkeletonBar className="mt-2 h-3 w-2/3" />
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function BoardListSkeleton(props: { rows: number; compact?: boolean }) {
    const { rows, compact } = props;
    const rowCount = Math.max(1, rows);
    const cellPad = compact ? 'px-2 py-1.5' : 'px-3 py-2.5';
    const titleWidths = ['w-48', 'w-56', 'w-40', 'w-64', 'w-44'];

    return (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <div className="overflow-x-auto">
                <table className={`w-full border-collapse ${compact ? 'text-xs' : 'text-sm'}`}>
                    <thead>
                        <tr className="border-b border-gray-200 bg-slate-50 text-left dark:border-gray-700 dark:bg-gray-800">
                            <th className={`${cellPad} min-w-[16rem]`}><SkeletonBar className="h-3 w-24" /></th>
                            <th className={cellPad}><SkeletonBar className="h-3 w-16" /></th>
                            <th className={cellPad}><SkeletonBar className="h-3 w-16" /></th>
                            <th className={cellPad}><SkeletonBar className="h-3 w-20" /></th>
                            <th className={cellPad}><SkeletonBar className="h-3 w-14" /></th>
                            <th className={cellPad}><SkeletonBar className="h-3 w-16" /></th>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.from({ length: rowCount }).map((_, index) => {
                            const zebra = index % 2 === 1
                                ? 'bg-slate-50/80 dark:bg-gray-800/80 dark:bg-gray-800/60'
                                : 'bg-white dark:bg-gray-900';
                            return (
                                <tr key={index} className={`border-b border-gray-100 dark:border-gray-800 ${zebra}`}>
                                    <td className={cellPad}>
                                        <div className="space-y-1.5">
                                            <SkeletonBar className={`h-3.5 ${titleWidths[index % titleWidths.length]} max-w-full`} />
                                            <SkeletonBar className="h-2.5 w-28 max-w-full" />
                                        </div>
                                    </td>
                                    <td className={cellPad}><SkeletonBar className="h-3 w-20" /></td>
                                    <td className={cellPad}><SkeletonBar className="h-6 w-6 rounded-full" /></td>
                                    <td className={cellPad}>
                                        <div className="space-y-1">
                                            <SkeletonBar className="h-4 w-24" />
                                            <SkeletonBar className="h-1.5 w-full" />
                                        </div>
                                    </td>
                                    <td className={cellPad}><SkeletonBar className="h-5 w-12" /></td>
                                    <td className={cellPad}><SkeletonBar className="h-5 w-16" /></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/**
 * Loading placeholder matching KanBan board or list layout.
 */
function BoardSkeleton(props: BoardSkeletonProps) {
    const {
        showList = false,
        compact = false,
        columns = 4,
        cardsPerColumn = 3,
        rows = 8,
    } = props;

    return (
        <div
            className="w-full overflow-x-auto"
            data-testid="board-skeleton"
            data-variant={showList ? 'list' : 'board'}
            aria-busy="true"
            aria-live="polite"
        >
            {showList ? (
                <BoardListSkeleton rows={rows} compact={compact} />
            ) : (
                <BoardColumnsSkeleton columns={columns} cardsPerColumn={cardsPerColumn} />
            )}
        </div>
    );
}

export default BoardSkeleton;
